import { useMemo } from 'react'
import { useWidget } from '../../../../../providers/widget'
import { useExplorer } from '../../../providers'
import { useFetchExplorerMeta, curateOptions, priceFieldForMode } from '../../../data'
import { ExploreWidgetConfig, VisitorFilter, compareDestinationNames } from '../../../config'
import { buildDestinationTabs, destinationFilterKeys, destinationOwnedKeys, resolveDestinationAppliedLabel, type DestinationFilterKey } from '../../../destinations/tabs'

export type WhereOption = { id: number | string; name: string }

/**
 * Destination ordering (#870, toolkit: `locations_order`).
 *
 * 'custom' is the pass-through: curateOptions has already applied the admin's
 * curated order, and an uncurated list keeps whatever order the feed returned.
 * 'alpha' sorts A-Z with localeCompare so accented names land where a reader
 * expects rather than after Z.
 */
export function orderWhereOptions<T extends WhereOption>(options: T[], order: 'custom' | 'alpha'): T[] {
  if (order !== 'alpha') return options
  // Collation comes from the shared comparator (see config.ts) so the
  // toolkit importer's baked CSV order and this runtime sort can never
  // tie-break differently; case/accent ties keep their input order
  // (Array.sort is stable). In zone mode each panel sorts independently —
  // the module's single interleaved A–Z list is not representable in the
  // two-panel Where field (same note the toolkit importer records for
  // both+alpha).
  return [...options].sort((a, b) => compareDestinationNames(a.name, b.name))
}
export type WhereSection = { key: string; label: string; filterKey: keyof VisitorFilter; options: WhereOption[] }

// Mirrors LocationFilter's SOURCE_CONFIG: which visitor-filter param each
// source writes and which filterOptions dimension curates it.
const SOURCE_CONFIG = {
  city: { filterKey: 'cities', curationKey: 'cities', label: 'Cities' },
  location: { filterKey: 'locations', curationKey: 'locations', label: 'Regions' },
  group: { filterKey: 'groups', curationKey: 'groups', label: 'Groups' },
  state: { filterKey: 'state', curationKey: 'states', label: 'States' },
  country: { filterKey: 'country', curationKey: 'countries', label: 'Countries' },
} as const

/** The visitor-filter keys a whereFieldSource OWNS — static, unlike sections,
 *  which drop dimensions whose curated option list is empty. Clearing and
 *  applied-state detection must use these keys, or a stored filter referencing
 *  a curated-out/meta-missing term becomes invisible and un-clearable. */
export const ownedWhereFilterKeys = (source: ExploreWidgetConfig['whereFieldSource']): Array<keyof VisitorFilter> => {
  if (source === 'zone') return ['locations', 'cities']
  return [SOURCE_CONFIG[source ?? 'city']?.filterKey ?? 'cities'] as Array<keyof VisitorFilter>
}

/** Destination option universe for the pro Where field, per whereFieldSource
 *  (zone = Regions + Cities sections), curated to the admin's selection+order. */
export function useWhereSections(): WhereSection[] {
  const { options, feedId } = useWidget<ExploreWidgetConfig>()
  const { data: metaData } = useFetchExplorerMeta(feedId, options.filter?.platform, priceFieldForMode(options.priceDisplay.noDates))
  const meta = metaData?.result
  const source = options.whereFieldSource ?? 'city'
  // Applied AFTER curation so 'custom' means "the curated order" and 'alpha'
  // overrides it — the toolkit's semantics for locations_order.
  const order = options.searchbar?.locationsOrder ?? 'custom'

  return useMemo<WhereSection[]>(() => {
    if (!meta) return []
    const curation = (key: keyof NonNullable<ExploreWidgetConfig['filterOptions']>) =>
      options.filterOptions?.[key]?.split(',').filter(Boolean)
    if (source === 'zone') {
      const regions = orderWhereOptions(curateOptions(meta.taxonomies?.locations ?? [], curation('locations')), order)
      const cities = orderWhereOptions(curateOptions(
        meta.location?.cities?.length ? meta.location.cities : meta.taxonomies?.cities ?? [],
        curation('cities'),
      ), order)
      return ([
        { key: 'locations', label: 'Our Regions', filterKey: 'locations', options: regions },
        { key: 'cities', label: 'All Cities', filterKey: 'cities', options: cities },
      ] as WhereSection[]).filter((s) => s.options.length > 0)
    }
    const cfg = SOURCE_CONFIG[source]
    let list: WhereOption[]
    if (source === 'location') list = meta.taxonomies?.locations ?? []
    else if (source === 'group') list = meta.taxonomies?.groups ?? []
    else if (source === 'state') list = meta.location?.states ?? []
    else if (source === 'country') list = meta.location?.countries ?? []
    else list = meta.location?.cities?.length ? meta.location.cities : meta.taxonomies?.cities ?? []
    const curated = orderWhereOptions(curateOptions(list, curation(cfg.curationKey)), order)
    return curated.length ? [{ key: cfg.curationKey, label: cfg.label, filterKey: cfg.filterKey as keyof VisitorFilter, options: curated }] : []
  }, [meta, source, order, options.filterOptions])
}

/** Names of the currently APPLIED destination selection, resolved against the
 *  option universe (empty string when nothing applied / nothing resolvable). */
export function useAppliedWhereLabel(sections: WhereSection[]): string {
  const { state } = useExplorer()
  return useMemo(() => {
    const names: string[] = []
    for (const section of sections) {
      const applied = (state.visitorFilters[section.filterKey] as string | undefined)?.split(',').filter(Boolean) ?? []
      for (const id of applied) {
        const opt = section.options.find((o) => String(o.id) === id)
        if (opt) names.push(opt.name)
      }
    }
    return names.join(', ')
  }, [sections, state.visitorFilters])
}

/**
 * Values sitting on the three destination dimensions that belong to a
 * DIFFERENT control, so the destination popup must not display, clear or
 * replace them (#1246 review):
 *
 *  - `visitorFilterDefaults` carries `tags`/`locations`/`cities` and
 *    widget.tsx seeds them into visitorFilters on load. That is the operator's
 *    baseline scope, not a visitor selection — reading it back as one made the
 *    Where field paint "Selected destination" with a clear × before the
 *    visitor touched anything, and that × wiped the scope.
 *  - `chipFilterKeys` overlaps all three and QuickChips writes them. A chip is
 *    an independent AND-able quick filter, not a destination.
 *
 * Shared by the field and the bar summary so the two cannot drift.
 */
export function useForeignDestinationValues(): Record<DestinationFilterKey, ReadonlySet<string>> {
  const { options } = useWidget<ExploreWidgetConfig>()
  const defaults = options.visitorFilterDefaults
  const chips = options.searchbar?.chips?.items
  return useMemo(() => {
    const out: Record<DestinationFilterKey, Set<string>> = {
      tags: new Set(), locations: new Set(), cities: new Set(),
    }
    for (const key of destinationFilterKeys) {
      const seeded = defaults?.[key]
      if (typeof seeded === 'string' && seeded) out[key].add(seeded)
    }
    for (const chip of chips ?? []) {
      const bucket = out[chip.filterKey as DestinationFilterKey]
      if (bucket) bucket.add(String(chip.centralId))
    }
    return out
  }, [defaults, chips])
}

/**
 * Destination-aware Where summary (#1072 D1). In destination mode the popup
 * writes tags/locations/cities ids that useAppliedWhereLabel's sections cannot
 * resolve (a city-source field has no tags section), so bar summaries reading
 * only the section resolver would say "Anywhere" while a destination filter is
 * active. Resolve through the stored tree first — the same rule ProWhereField
 * uses for the field itself, over the same owned-key cut — and fall back to
 * the section resolver outside destination mode.
 */
export function useDestinationAwareWhereLabel(sections: WhereSection[]): string {
  const { state } = useExplorer()
  const { options } = useWidget<ExploreWidgetConfig>()
  const sectionLabel = useAppliedWhereLabel(sections)
  const foreign = useForeignDestinationValues()
  const destinationTabs = useMemo(
    () => buildDestinationTabs(options.destinationTree, options.destinations?.groupOrder),
    [options.destinationTree, options.destinations?.groupOrder],
  )
  const destinationMode = Boolean(options.destinations?.enabled) && destinationTabs.length > 0
  return useMemo(() => {
    if (!destinationMode) return sectionLabel
    const owned = destinationOwnedKeys(
      (key) => state.visitorFilters[key] as string | undefined,
      (key) => foreign[key],
    )
    const label = resolveDestinationAppliedLabel(
      destinationTabs,
      (key) => state.visitorFilters[key] as string | undefined,
      owned,
    )
    if (label) return label
    // A stale/curated-out id the POPUP wrote must still read as applied,
    // matching the field. An operator default or a live chip must not.
    const hasApplied = owned.some((key) => Boolean(state.visitorFilters[key] as string | undefined))
    return hasApplied ? 'Selected destination' : ''
  }, [destinationMode, sectionLabel, destinationTabs, state.visitorFilters, foreign])
}
